import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import PaginationBar from "@/components/PaginationBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  KeyRound,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  Store,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const EMPTY_CLIENT_FORM = {
  username: "",
  name: "",
  email: "",
  password: "",
  company: "",
  phone: "",
  initial_branch: "MADRID",
  initial_erp_customer_code: "",
};

const EMPTY_RESTAURANT_FORM = {
  user_id: "",
  name: "",
  address: "",
  notes: "",
  branch: "MADRID",
  erp_customer_code: "",
  show_prices_to_client: true,
};

export default function AdminClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientRestaurants, setClientRestaurants] = useState({});
  const [restaurantsLoading, setRestaurantsLoading] = useState({});
  const [clientDialog, setClientDialog] = useState({ open: false, mode: "create", client: null });
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT_FORM);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, client: null });
  const [newPassword, setNewPassword] = useState("");
  const [deleteClientDialog, setDeleteClientDialog] = useState({ open: false, client: null });
  const [restaurantDialog, setRestaurantDialog] = useState({
    open: false,
    mode: "create",
    client: null,
    restaurant: null,
  });
  const [restaurantForm, setRestaurantForm] = useState(EMPTY_RESTAURANT_FORM);
  const [deleteRestaurantDialog, setDeleteRestaurantDialog] = useState({
    open: false,
    client: null,
    restaurant: null,
  });

  useEffect(() => {
    loadClients();
  }, [page, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const [clientsRes, countRes] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, search },
        }),
        axios.get(`${API}/admin/users/count`, { params: { search } }),
      ]);

      const clientList = clientsRes.data || [];
      setClients(clientList);
      setTotalCount(countRes?.data?.count ?? 0);

      if (clientList.length > 0) {
        const restaurantResponses = await Promise.all(
          clientList.map(async (client) => {
            try {
              const response = await axios.get(`${API}/admin/users/${client.user_id}/restaurants`);
              return [client.user_id, response.data || []];
            } catch {
              return [client.user_id, []];
            }
          }),
        );

        setClientRestaurants((prev) => ({
          ...prev,
          ...Object.fromEntries(restaurantResponses),
        }));
      }
    } catch {
      toast.error("Error al cargar los clientes");
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurants = async (userId, force = false) => {
    if (!force && clientRestaurants[userId]) return;

    setRestaurantsLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/restaurants`);
      setClientRestaurants((prev) => ({ ...prev, [userId]: response.data || [] }));
    } catch {
      toast.error("Error al cargar los restaurantes");
    } finally {
      setRestaurantsLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const toggleExpand = async (userId) => {
    const nextExpanded = expandedClient === userId ? null : userId;
    setExpandedClient(nextExpanded);
    if (nextExpanded) await loadRestaurants(userId);
  };

  const openCreateClient = () => {
    setClientForm(EMPTY_CLIENT_FORM);
    setClientDialog({ open: true, mode: "create", client: null });
  };

  const openEditClient = (client) => {
    setClientForm({
      username: client.username || "",
      name: client.name || "",
      email: client.email || "",
      password: "",
      company: client.company || "",
      phone: client.phone || "",
      initial_branch: "MADRID",
      initial_erp_customer_code: "",
    });
    setClientDialog({ open: true, mode: "edit", client });
  };

  const handleClientSubmit = async () => {
    if (!clientForm.username.trim() || !clientForm.name.trim() || !clientForm.email.trim()) {
      toast.error("Completa usuario, nombre y email");
      return;
    }

    if (clientDialog.mode === "create" && clientForm.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setSaving(true);
    try {
      if (clientDialog.mode === "create") {
        await axios.post(`${API}/admin/users`, {
          username: clientForm.username.trim().toLowerCase(),
          name: clientForm.name.trim(),
          email: clientForm.email.trim().toLowerCase(),
          password: clientForm.password,
          company: clientForm.company.trim(),
          phone: clientForm.phone.trim(),
          branch: clientForm.initial_branch,
          erp_customer_code: clientForm.initial_erp_customer_code.trim(),
        });
        toast.success("Cliente creado");
      } else if (clientDialog.client) {
        await axios.put(`${API}/admin/users/${clientDialog.client.user_id}`, {
          username: clientForm.username.trim().toLowerCase(),
          name: clientForm.name.trim(),
          email: clientForm.email.trim().toLowerCase(),
          company: clientForm.company.trim(),
          phone: clientForm.phone.trim(),
        });
        toast.success("Cliente actualizado");
      }

      setClientDialog({ open: false, mode: "create", client: null });
      setClientForm(EMPTY_CLIENT_FORM);
      await loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar el cliente");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6 || !passwordDialog.client) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${passwordDialog.client.user_id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success("Contraseña actualizada");
      setPasswordDialog({ open: false, client: null });
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteClientDialog.client) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteClientDialog.client.user_id}`);
      toast.success("Cliente eliminado");
      setDeleteClientDialog({ open: false, client: null });
      await loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el cliente");
    } finally {
      setSaving(false);
    }
  };

  const openCreateRestaurant = (client) => {
    setRestaurantForm({
      ...EMPTY_RESTAURANT_FORM,
      user_id: client.user_id,
      name: client.company || client.name || "",
    });
    setRestaurantDialog({ open: true, mode: "create", client, restaurant: null });
  };

  const openEditRestaurant = (client, restaurant) => {
    setRestaurantForm({
      user_id: client.user_id,
      name: restaurant.name || "",
      address: restaurant.address || "",
      notes: restaurant.notes || "",
      branch: restaurant.branch || "MADRID",
      erp_customer_code: restaurant.erp_customer_code || "",
      show_prices_to_client: restaurant.show_prices_to_client !== false,
    });
    setRestaurantDialog({ open: true, mode: "edit", client, restaurant });
  };

  const handleToggleRestaurantPrices = async (client, restaurant, nextValue) => {
    try {
      await axios.put(`${API}/admin/restaurants/${restaurant.restaurant_id}`, {
        show_prices_to_client: nextValue,
      });
      setClientRestaurants((prev) => ({
        ...prev,
        [client.user_id]: (prev[client.user_id] || []).map((item) =>
          item.restaurant_id === restaurant.restaurant_id
            ? { ...item, show_prices_to_client: nextValue }
            : item,
        ),
      }));
      toast.success(`Precios ${nextValue ? "visibles" : "ocultos"} para ${restaurant.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar la visibilidad de precios");
    }
  };

  const handleRestaurantSubmit = async () => {
    if (!restaurantDialog.client || !restaurantForm.name.trim()) {
      toast.error("El restaurante necesita al menos un nombre");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_id: restaurantDialog.client.user_id,
        name: restaurantForm.name.trim(),
        address: restaurantForm.address.trim(),
        notes: restaurantForm.notes.trim(),
        branch: restaurantForm.branch,
        erp_customer_code: restaurantForm.erp_customer_code.trim(),
        show_prices_to_client: restaurantForm.show_prices_to_client,
      };

      if (restaurantDialog.mode === "create") {
        await axios.post(`${API}/admin/restaurants`, payload);
        toast.success("Restaurante creado");
      } else if (restaurantDialog.restaurant) {
        await axios.put(`${API}/admin/restaurants/${restaurantDialog.restaurant.restaurant_id}`, payload);
        toast.success("Restaurante actualizado");
      }

      setRestaurantDialog({ open: false, mode: "create", client: null, restaurant: null });
      setRestaurantForm(EMPTY_RESTAURANT_FORM);
      await loadRestaurants(restaurantDialog.client.user_id, true);
      setExpandedClient(restaurantDialog.client.user_id);
      await loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar el restaurante");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!deleteRestaurantDialog.client || !deleteRestaurantDialog.restaurant) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/admin/restaurants/${deleteRestaurantDialog.restaurant.restaurant_id}`);
      toast.success("Restaurante eliminado");
      await loadRestaurants(deleteRestaurantDialog.client.user_id, true);
      setDeleteRestaurantDialog({ open: false, client: null, restaurant: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el restaurante");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-clients-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100">
              <Users className="h-6 w-6 text-blue-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Clientes y restaurantes</h1>
              <p className="text-sm text-slate-500" data-testid="clients-total-count">
                {totalCount} cuentas de cliente activas
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Buscar por nombre, usuario, email o empresa"
                className="pl-10"
                data-testid="clients-search-input"
              />
            </div>
            <Button
              onClick={openCreateClient}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="create-client-button"
            >
              <Plus className="h-4 w-4" />
              Nuevo cliente
            </Button>
          </div>
        </div>

        {totalCount > PAGE_SIZE && !loading && (
          <PaginationBar
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPrev={() => setPage((prev) => Math.max(0, prev - 1))}
            onNext={() => setPage((prev) => prev + 1)}
          />
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500" data-testid="clients-loading-state">
                Cargando clientes...
              </div>
            ) : clients.length === 0 ? (
              <div className="p-8 text-center text-slate-500" data-testid="clients-empty-state">
                No hay clientes para mostrar.
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {clients.map((client) => {
                  const isExpanded = expandedClient === client.user_id;
                  const restaurants = clientRestaurants[client.user_id] || [];
                  const restaurantLoading = restaurantsLoading[client.user_id];

                  return (
                    <div key={client.user_id} className="bg-white" data-testid={`client-row-${client.user_id}`}>
                      <div className="flex flex-col gap-4 p-5 xl:flex-row xl:items-center xl:justify-between">
                        <button
                          type="button"
                          onClick={() => toggleExpand(client.user_id)}
                          className="flex flex-1 items-start gap-4 text-left"
                          data-testid={`toggle-client-${client.user_id}`}
                        >
                          <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                            <UserRound className="h-5 w-5" />
                          </div>

                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-lg font-semibold text-slate-900" data-testid={`client-name-${client.user_id}`}>
                                {client.name}
                              </span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600" data-testid={`client-username-${client.user_id}`}>
                                @{client.username}
                              </span>
                              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700" data-testid={`client-restaurant-count-${client.user_id}`}>
                                {restaurants.length} restaurante{restaurants.length === 1 ? "" : "s"}
                              </span>
                            </div>

                            <div className="grid gap-2 text-sm text-slate-500 md:grid-cols-2">
                              <div className="flex items-center gap-2" data-testid={`client-email-${client.user_id}`}>
                                <Mail className="h-4 w-4" />
                                <span className="truncate">{client.email}</span>
                              </div>
                              <div className="flex items-center gap-2" data-testid={`client-phone-${client.user_id}`}>
                                <Phone className="h-4 w-4" />
                                <span>{client.phone || "Sin teléfono"}</span>
                              </div>
                            </div>

                            {client.company && (
                              <p className="text-sm text-slate-600" data-testid={`client-company-${client.user_id}`}>
                                {client.company}
                              </p>
                            )}
                          </div>

                          <div className="pt-1 text-slate-400">
                            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </div>
                        </button>

                        <div className="flex flex-wrap gap-2 xl:justify-end">
                          <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => openEditClient(client)}
                            data-testid={`edit-client-${client.user_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                            Editar cuenta
                          </Button>
                          <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => {
                              setPasswordDialog({ open: true, client });
                              setNewPassword("");
                            }}
                            data-testid={`reset-client-password-${client.user_id}`}
                          >
                            <KeyRound className="h-4 w-4" />
                            Contraseña
                          </Button>
                          <Button
                            variant="outline"
                            className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteClientDialog({ open: true, client })}
                            data-testid={`delete-client-${client.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/70 p-5" data-testid={`client-restaurants-${client.user_id}`}>
                          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                              <h2 className="text-base font-semibold text-slate-900">Restaurantes asociados</h2>
                              <p className="text-sm text-slate-500">
                                Cada restaurante tiene su propia sede, dirección y catálogo.
                              </p>
                            </div>

                            <Button
                              onClick={() => openCreateRestaurant(client)}
                              className="gap-2 bg-slate-900 hover:bg-slate-800"
                              data-testid={`create-restaurant-${client.user_id}`}
                            >
                              <Plus className="h-4 w-4" />
                              Añadir restaurante
                            </Button>
                          </div>

                          {restaurantLoading ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500" data-testid={`restaurants-loading-${client.user_id}`}>
                              Cargando restaurantes...
                            </div>
                          ) : restaurants.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500" data-testid={`restaurants-empty-${client.user_id}`}>
                              Este cliente todavía no tiene restaurantes.
                            </div>
                          ) : (
                            <div className="grid gap-4 lg:grid-cols-2">
                              {restaurants.map((restaurant) => (
                                <div
                                  key={restaurant.restaurant_id}
                                  className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
                                  data-testid={`restaurant-card-${restaurant.restaurant_id}`}
                                >
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                                        <Store className="h-5 w-5" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-slate-900" data-testid={`restaurant-name-${restaurant.restaurant_id}`}>
                                          {restaurant.name}
                                        </p>
                                        <p className="text-xs text-slate-500">ID restaurante: {restaurant.restaurant_id.slice(0, 8)}</p>
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 text-xs">
                                      <span className="rounded-full bg-indigo-50 px-2.5 py-1 font-medium text-indigo-700" data-testid={`restaurant-branch-${restaurant.restaurant_id}`}>
                                        {restaurant.branch || "Sin sede"}
                                      </span>
                                      <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700" data-testid={`restaurant-erp-${restaurant.restaurant_id}`}>
                                        A3: {restaurant.erp_customer_code || "Pendiente"}
                                      </span>
                                      <span className={`rounded-full px-2.5 py-1 font-medium ${restaurant.show_prices_to_client !== false ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`restaurant-show-prices-badge-${restaurant.restaurant_id}`}>
                                        Precios cliente: {restaurant.show_prices_to_client !== false ? "Sí" : "No"}
                                      </span>
                                    </div>

                                    <div className="flex items-start gap-2 text-sm text-slate-500" data-testid={`restaurant-address-${restaurant.restaurant_id}`}>
                                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                      <span>{restaurant.address || "Dirección pendiente"}</span>
                                    </div>

                                    {restaurant.notes && (
                                      <p className="rounded-2xl bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid={`restaurant-notes-${restaurant.restaurant_id}`}>
                                        {restaurant.notes}
                                      </p>
                                    )}
                                  </div>

                                  <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <div>
                                      <p className="text-sm font-medium text-slate-900" data-testid={`restaurant-show-prices-label-${restaurant.restaurant_id}`}>
                                        Mostrar precios al cliente
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Se aplica al catálogo, carrito, historial, Mi Gasto y descargas del pedido.
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium text-slate-700" data-testid={`restaurant-show-prices-value-${restaurant.restaurant_id}`}>
                                        {restaurant.show_prices_to_client !== false ? "Sí" : "No"}
                                      </span>
                                      <Switch
                                        checked={restaurant.show_prices_to_client !== false}
                                        onCheckedChange={(checked) => handleToggleRestaurantPrices(client, restaurant, checked)}
                                        data-testid={`restaurant-show-prices-switch-${restaurant.restaurant_id}`}
                                      />
                                    </div>
                                  </div>

                                  <div className="mt-5 flex flex-wrap gap-2">
                                    <Button
                                      onClick={() => navigate(`/admin/restaurants/${restaurant.restaurant_id}/products`)}
                                      className="gap-2 bg-blue-600 hover:bg-blue-700"
                                      data-testid={`manage-products-${restaurant.restaurant_id}`}
                                    >
                                      <Store className="h-4 w-4" />
                                      Gestionar catálogo
                                    </Button>
                                    <Button
                                      variant="outline"
                                      className="gap-2"
                                      onClick={() => openEditRestaurant(client, restaurant)}
                                      data-testid={`edit-restaurant-${restaurant.restaurant_id}`}
                                    >
                                      <Pencil className="h-4 w-4" />
                                      Editar
                                    </Button>
                                    <Button
                                      variant="outline"
                                      className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                      onClick={() => setDeleteRestaurantDialog({ open: true, client, restaurant })}
                                      data-testid={`delete-restaurant-${restaurant.restaurant_id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Eliminar
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={clientDialog.open} onOpenChange={(open) => {
          setClientDialog((prev) => ({ ...prev, open }));
          if (!open) setClientForm(EMPTY_CLIENT_FORM);
        }}>
          <DialogContent className="sm:max-w-2xl" data-testid="client-dialog">
            <DialogHeader>
              <DialogTitle>{clientDialog.mode === "create" ? "Crear cliente" : "Editar cuenta del cliente"}</DialogTitle>
              <DialogDescription>
                {clientDialog.mode === "create"
                  ? "Se creará también un restaurante inicial que luego podrás editar o ampliar."
                  : "Los datos de sede y A3 ahora se gestionan dentro de cada restaurante."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="client-username">Usuario</Label>
                <Input id="client-username" value={clientForm.username} onChange={(event) => setClientForm((prev) => ({ ...prev, username: event.target.value }))} data-testid="client-form-username" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-name">Nombre</Label>
                <Input id="client-name" value={clientForm.name} onChange={(event) => setClientForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="client-form-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-email">Email</Label>
                <Input id="client-email" type="email" value={clientForm.email} onChange={(event) => setClientForm((prev) => ({ ...prev, email: event.target.value }))} data-testid="client-form-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-phone">Teléfono</Label>
                <Input id="client-phone" value={clientForm.phone} onChange={(event) => setClientForm((prev) => ({ ...prev, phone: event.target.value }))} data-testid="client-form-phone" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="client-company">Empresa</Label>
                <Input id="client-company" value={clientForm.company} onChange={(event) => setClientForm((prev) => ({ ...prev, company: event.target.value }))} data-testid="client-form-company" />
              </div>

              {clientDialog.mode === "create" && (
                <>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="client-password">Contraseña inicial</Label>
                    <Input id="client-password" type="password" value={clientForm.password} onChange={(event) => setClientForm((prev) => ({ ...prev, password: event.target.value }))} data-testid="client-form-password" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial-branch">Sede del restaurante inicial</Label>
                    <select id="initial-branch" value={clientForm.initial_branch} onChange={(event) => setClientForm((prev) => ({ ...prev, initial_branch: event.target.value }))} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="client-form-initial-branch">
                      <option value="MADRID">MADRID</option>
                      <option value="GUADALAJARA">GUADALAJARA</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initial-erp">Código A3 del restaurante inicial</Label>
                    <Input id="initial-erp" value={clientForm.initial_erp_customer_code} onChange={(event) => setClientForm((prev) => ({ ...prev, initial_erp_customer_code: event.target.value }))} placeholder="Ej: CLI001" data-testid="client-form-initial-erp" />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setClientDialog({ open: false, mode: "create", client: null })} data-testid="client-dialog-cancel">Cancelar</Button>
              <Button onClick={handleClientSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="client-dialog-save">
                {saving ? "Guardando..." : clientDialog.mode === "create" ? "Crear cliente" : "Guardar cambios"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={passwordDialog.open} onOpenChange={(open) => {
          setPasswordDialog((prev) => ({ ...prev, open }));
          if (!open) setNewPassword("");
        }}>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Restablecer contraseña</DialogTitle>
              <DialogDescription>Nueva contraseña para {passwordDialog.client?.name || "este cliente"}.</DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="reset-password-input">Nueva contraseña</Label>
              <Input id="reset-password-input" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} data-testid="reset-password-input" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordDialog({ open: false, client: null })} data-testid="reset-password-cancel">Cancelar</Button>
              <Button onClick={handleResetPassword} disabled={saving} data-testid="reset-password-save">{saving ? "Guardando..." : "Guardar contraseña"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteClientDialog.open} onOpenChange={(open) => setDeleteClientDialog((prev) => ({ ...prev, open }))}>
          <DialogContent data-testid="delete-client-dialog">
            <DialogHeader>
              <DialogTitle>Eliminar cliente</DialogTitle>
              <DialogDescription>Se eliminarán también sus restaurantes, asignaciones y pedidos asociados.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-slate-600" data-testid="delete-client-message">¿Seguro que quieres eliminar a {deleteClientDialog.client?.name || "este cliente"}?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteClientDialog({ open: false, client: null })} data-testid="delete-client-cancel">Cancelar</Button>
              <Button onClick={handleDeleteClient} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="delete-client-confirm">{saving ? "Eliminando..." : "Eliminar cliente"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={restaurantDialog.open} onOpenChange={(open) => {
          setRestaurantDialog((prev) => ({ ...prev, open }));
          if (!open) setRestaurantForm(EMPTY_RESTAURANT_FORM);
        }}>
          <DialogContent className="sm:max-w-2xl" data-testid="restaurant-dialog">
            <DialogHeader>
              <DialogTitle>{restaurantDialog.mode === "create" ? "Añadir restaurante" : "Editar restaurante"}</DialogTitle>
              <DialogDescription>Configura la información operativa y el catálogo de esta sede.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="restaurant-name">Nombre</Label>
                <Input id="restaurant-name" value={restaurantForm.name} onChange={(event) => setRestaurantForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="restaurant-form-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="restaurant-branch">Sede</Label>
                <select id="restaurant-branch" value={restaurantForm.branch} onChange={(event) => setRestaurantForm((prev) => ({ ...prev, branch: event.target.value }))} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="restaurant-form-branch">
                  <option value="MADRID">MADRID</option>
                  <option value="GUADALAJARA">GUADALAJARA</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="restaurant-erp">Código cliente A3</Label>
                <Input id="restaurant-erp" value={restaurantForm.erp_customer_code} onChange={(event) => setRestaurantForm((prev) => ({ ...prev, erp_customer_code: event.target.value }))} placeholder="Ej: CLI001" data-testid="restaurant-form-erp" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="restaurant-address">Dirección</Label>
                <Textarea id="restaurant-address" value={restaurantForm.address} onChange={(event) => setRestaurantForm((prev) => ({ ...prev, address: event.target.value }))} className="min-h-[90px]" data-testid="restaurant-form-address" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="restaurant-notes">Notas internas</Label>
                <Textarea id="restaurant-notes" value={restaurantForm.notes} onChange={(event) => setRestaurantForm((prev) => ({ ...prev, notes: event.target.value }))} className="min-h-[90px]" data-testid="restaurant-form-notes" />
              </div>
              <div className="space-y-3 md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <Label htmlFor="restaurant-show-prices">Mostrar precios al cliente</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Si lo desactivas, el restaurante verá cantidades pero no importes en cliente ni en CSV/PDF del pedido.
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium text-slate-700" data-testid="restaurant-form-show-prices-value">
                    {restaurantForm.show_prices_to_client ? "Sí" : "No"}
                  </span>
                  <Switch
                    id="restaurant-show-prices"
                    checked={restaurantForm.show_prices_to_client}
                    onCheckedChange={(checked) => setRestaurantForm((prev) => ({ ...prev, show_prices_to_client: checked }))}
                    data-testid="restaurant-form-show-prices-switch"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setRestaurantDialog({ open: false, mode: "create", client: null, restaurant: null })} data-testid="restaurant-dialog-cancel">Cancelar</Button>
              <Button onClick={handleRestaurantSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="restaurant-dialog-save">{saving ? "Guardando..." : restaurantDialog.mode === "create" ? "Crear restaurante" : "Guardar cambios"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteRestaurantDialog.open} onOpenChange={(open) => setDeleteRestaurantDialog((prev) => ({ ...prev, open }))}>
          <DialogContent data-testid="delete-restaurant-dialog">
            <DialogHeader>
              <DialogTitle>Eliminar restaurante</DialogTitle>
              <DialogDescription>Se eliminarán también sus productos asignados.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-slate-600" data-testid="delete-restaurant-message">¿Seguro que quieres eliminar {deleteRestaurantDialog.restaurant?.name || "este restaurante"}?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteRestaurantDialog({ open: false, client: null, restaurant: null })} data-testid="delete-restaurant-cancel">Cancelar</Button>
              <Button onClick={handleDeleteRestaurant} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="delete-restaurant-confirm">{saving ? "Eliminando..." : "Eliminar restaurante"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}